import {
  Box,
  Step,
  StepConnector,
  StepLabel,
  Stepper as MuiStepper,
  type StepIconProps,
} from '@mui/material';
import { forwardRef } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

/**
 * FSE Stepper — MD3 progress stepper for multi-step capture flows.
 *
 * Spec: `Documentation/Claude design files/preview/stepper.html`. Renders a row
 * of 28dp dots joined by 2dp segments, with a small label under each step:
 *   - done    → leaf `secondary` fill + check icon
 *   - current → leaf `primary` fill + number, ringed with `primaryContainer`
 *   - future  → `surfaceContainerHigh` fill + number
 * Done segments adopt the `secondary` colour; the rest use `outlineVariant`.
 *
 * The canonical FSE `Stepper`: takes a `steps` array + `activeStep` index
 * (declarative) rather than the MUI compound `<Stepper><Step><StepLabel>` API.
 * Owns the public prop surface — consumers never touch MUI's `sx`. Exposed
 * through @fse/ui so consumers never import @mui/material directly
 * (swap-promise); the raw MUI Stepper re-export was dropped on 2026-06-04
 * (TD-089) once every onboarding flow moved onto this component.
 */
export interface StepperProps {
  /** Ordered step labels (also drives the count of dots). */
  steps: string[];
  /** Zero-based index of the in-progress step. */
  activeStep: number;
}

// Mode-aware step-icon factory: closes over the active mode's MD3 roles so the
// dots tint correctly in both light + dark. In light, primary=leaf[700],
// secondary=leaf[600] and primaryContainer=leaf[100] — unchanged from before.
function makeStepIcon(sys: (typeof mdSysColor)['light' | 'dark']) {
  return function FseStepIcon({ active, completed, icon }: StepIconProps) {
    const background = completed
      ? sys.secondary // leaf[600]
      : active
        ? sys.primary // leaf[700]
        : sys.surfaceContainerHigh;
    const foreground = completed || active ? sys.onPrimary : sys.onSurfaceVariant;

    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '999px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: background,
          color: foreground,
          fontFamily: typography.fontFamily,
          fontWeight: 700,
          fontSize: `${typography.labelMedium.fontSize}px`,
          fontVariantNumeric: 'tabular-nums',
          ...(active
            ? { outline: `3px solid ${sys.primaryContainer}`, outlineOffset: '1px' }
            : {}),
        }}
      >
        {completed ? (
          <Icon name="check" size={16} color="inherit" />
        ) : (
          icon
        )}
      </Box>
    );
  };
}

export const Stepper = forwardRef<HTMLDivElement, StepperProps>(
  function Stepper({ steps, activeStep }, ref) {
    const { mode } = useColorMode();
    const sys = mdSysColor[mode];
    const stepIcon = makeStepIcon(sys);
    const connector = (
      <StepConnector
        sx={{
          '& .MuiStepConnector-line': {
            borderTopWidth: 2,
            borderRadius: '999px',
            borderColor: sys.outlineVariant,
          },
          '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
            borderColor: sys.secondary,
          },
        }}
      />
    );
    return (
      <MuiStepper
        ref={ref}
        activeStep={activeStep}
        alternativeLabel
        connector={connector}
        sx={{ width: '100%' }}
      >
        {steps.map((label, index) => (
          <Step key={`${label}-${index}`}>
            <StepLabel
              slots={{ stepIcon }}
              sx={{
                '& .MuiStepLabel-label': {
                  fontFamily: typography.fontFamily,
                  fontSize: `${typography.labelSmall.fontSize}px`,
                  color: sys.onSurfaceVariant,
                  mt: '6px !important',
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: sys.primary,
                  fontWeight: 700,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </MuiStepper>
    );
  },
);
